import { useNavigate } from "react-router-dom";
import Tag from "@/components/Tag";
import { TABLE_CARD_BODY_CELL, TableCard, TableCardMobileStack, TableCardRow } from "@/components/Table";
import { CardSkeleton } from "@/components/Skeleton";
import EmptyState from "@/components/EmptyState";
import { toDateFormat } from "@/lib/dateHelpers";
import type { CareerServicesRecentInteraction } from "../queries";
import { interactionPathForCareerInteraction } from "../_utils";

type Props = {
  interactions: CareerServicesRecentInteraction[];
  loading?: boolean;
};

export default function RecentInteractionsCard({ interactions, loading = false }: Props) {
  const navigate = useNavigate();

  return (
    <TableCard title="Recent interactions" icon="forum" testId="career-services-recent-interactions">
      {loading && interactions.length === 0 ? (
        <CardSkeleton />
      ) : interactions.length === 0 ? (
        <EmptyState title="No interactions match these filters." iconName="forum" />
      ) : (
        <table className="w-full" data-testid="career-services-recent-interactions-table">
          <tbody>
            {interactions.map((interaction) => {
              const path = interactionPathForCareerInteraction(interaction);
              return (
                <TableCardRow
                  key={interaction.id}
                  columnCount={4}
                  mobile={
                    <TableCardMobileStack
                      lines={[
                        `${interaction.mode ?? "—"} · ${interaction.ownerName ?? "Unassigned"}`,
                        `${toDateFormat(interaction.interactionDate) || ""} · ${interaction.categories.join(", ")}`
                      ]}
                    />
                  }
                  onClick={path ? () => navigate(path) : undefined}
                  ariaLabel={path ? `View interaction from ${interaction.mode ?? "unknown mode"}` : undefined}
                >
                  <td className={TABLE_CARD_BODY_CELL}>{toDateFormat(interaction.interactionDate) ?? "—"}</td>
                  <td className={TABLE_CARD_BODY_CELL}>{interaction.mode ?? "—"}</td>
                  <td className={TABLE_CARD_BODY_CELL}>
                    <div className="flex flex-wrap gap-1">
                      {interaction.categories.map((category) => (
                        <Tag key={category} tagType="secondary">
                          {category}
                        </Tag>
                      ))}
                    </div>
                  </td>
                  <td className={`${TABLE_CARD_BODY_CELL} text-sys-brand-on-surface-variant`}>
                    {interaction.ownerName ?? "—"}
                  </td>
                </TableCardRow>
              );
            })}
          </tbody>
        </table>
      )}
    </TableCard>
  );
}
